import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Node } from "react-checkbox-tree";
import { useSelector } from "react-redux";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { CheckboxTree } from "ui";

import { selectLayersByVersionId, useGetProjectQuery, useGetVersionConfigQuery, useGetVersionQuery } from "fond/api";
import { InferredIcon } from "fond/map/Legend/InferredIcon";
import { GroupConfig, LayerIds, Store } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { generateCheckboxTreeItems } from "fond/utils/configurations";
import { BlockSpinner } from "fond/widgets";

import { Container } from "./LayerTree.styles";
import { HeaderLabel, LayerBoxHeader, SelectedText, StyledCaption } from "./NewExportModal/NewExportModal.styles";

interface LayerTreeProps {
  excludedKeys?: string[];
  onSelectionChange: (selected: string[]) => void;
  versionId: string | undefined;
  title?: string;
  showIcons?: boolean;
  /**
   * Optional adornment rendered on the right side of each leaf (LAYER) row.
   */
  renderLeafAdornment?: (layerKey: string) => ReactNode;
}

const LayerTree: React.FC<LayerTreeProps> = ({
  excludedKeys = [],
  onSelectionChange,
  showIcons = false,
  title = "",
  versionId,
  renderLeafAdornment,
}: LayerTreeProps) => {
  const { data: config, isLoading, isFetching } = useGetVersionConfigQuery(versionId ?? skipToken, { skip: !versionId });
  const { data: version } = useGetVersionQuery(versionId ?? skipToken, { skip: !versionId });
  const { data: project } = useGetProjectQuery(version?.Project ?? skipToken, { skip: !versionId || !version?.Project });
  const layers = useSelector((state: Store) => (versionId ? selectLayersByVersionId(state, versionId) : null));
  const [checked, setChecked] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const treeRef = useRef<CheckboxTree>(null);

  // Get all the existent layer keys from the layers
  const existentLayerKeys = useMemo(() => {
    if (!layers) return null;
    const layerKeys = layers.map<string>((layer) => layer.LayerKey).filter(Boolean);

    // Input Data Area layer doesn't exist in the layers list but in the version field
    if (project?.Type === "planner" && version?.UploadedArea) {
      return [...layerKeys, LayerIds.inputDataArea];
    }
    return layerKeys;
  }, [layers, project?.Type, version?.UploadedArea]);

  const renderIcon = useCallback(
    (entity: GroupConfig | LayerConfig | SublayerConfig) =>
      config && entity.Type !== "GROUP" ? <InferredIcon entity={entity} config={config} /> : undefined,
    [config]
  );

  const treeItems = useMemo(() => {
    if (!config) return [];
    const items = generateCheckboxTreeItems({
      config,
      excludedKeys,
      enabledKeys: existentLayerKeys,
      renderIcon: showIcons ? renderIcon : undefined,
    });
    if (!renderLeafAdornment) return items;

    const decorate = (node: Node): Node => {
      if (node.children && node.children.length > 0) {
        return { ...node, children: node.children.map(decorate) };
      }
      return {
        ...node,
        label: (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flex: 1, minWidth: 0, gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{node.label}</Box>
            {renderLeafAdornment(node.value)}
          </Box>
        ),
      };
    };
    return items.map(decorate);
  }, [config, excludedKeys, existentLayerKeys, showIcons, renderIcon, renderLeafAdornment]);

  const allIds = useMemo(() => {
    const ids: string[] = [];
    const traverse = (nodes: typeof treeItems) => {
      nodes.forEach((node) => {
        if (node.children) {
          traverse(node.children);
        } else if (!node.disabled) {
          ids.push(node.value);
        }
      });
    };
    traverse(treeItems);
    return ids;
  }, [treeItems]);

  const numTotalLayer = allIds.length;
  const numSelectedLayer = checked.length;

  // This effect is used to expand all nodes when the treeItems change
  useEffect(() => {
    treeRef.current?.onExpandAll();
  }, [treeItems]);

  const handleOnCheck = (nodeIds: string[]) => {
    setChecked(nodeIds);
    onSelectionChange(nodeIds);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const allChecked = !event.target.checked;
    handleOnCheck(allChecked ? [] : allIds);
  };

  if (isLoading || isFetching) return <BlockSpinner containerProps={{ height: "100%" }} />;

  return (
    <Container>
      {versionId ? (
        <LayerBoxHeader>
          <Box display="flex" alignItems="center">
            <FormControlLabel
              sx={{ height: 32 }}
              label={
                <Box>
                  <HeaderLabel>{title}</HeaderLabel>
                </Box>
              }
              control={
                <Checkbox
                  size="small"
                  data-testid="export-select-all-layers"
                  checked={numTotalLayer > 0 && numTotalLayer === numSelectedLayer}
                  indeterminate={numSelectedLayer > 0 && numTotalLayer !== numSelectedLayer}
                  onChange={handleSelectAll}
                />
              }
            />
            <StyledCaption>{`(${numTotalLayer} Layers)`}</StyledCaption>
          </Box>
          <SelectedText>{`${numSelectedLayer} Selected`}</SelectedText>
        </LayerBoxHeader>
      ) : (
        <LayerBoxHeader>
          <Box display="flex" alignItems="center">
            <Checkbox size="small" />
            <HeaderLabel sx={{ pr: 1 }}>Name</HeaderLabel>
            <StyledCaption>(Layers)</StyledCaption>
          </Box>
        </LayerBoxHeader>
      )}

      <CheckboxTree ref={treeRef} checked={checked} expanded={expanded} onCheck={handleOnCheck} onExpand={setExpanded} nodes={treeItems} />
    </Container>
  );
};
export default LayerTree;
